import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  signInWithGoogle, 
  signUpWithEmail, 
  signInWithEmail,
  sendUserPasswordResetEmail,
  sendUserPasswordResetEmailSimple
} from '../../services/authService';

const UserAuth = ({ isOpen, onClose, darkMode }) => {
  const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'forgot-password'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    confirmPassword: ''
  });
  const [resetEmail, setResetEmail] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        onClose();
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('🔧 Form submitted with mode:', authMode);
      console.log('📋 Form data:', { 
        email: formData.email, 
        displayName: formData.displayName,
        passwordLength: formData.password?.length,
        confirmPasswordLength: formData.confirmPassword?.length
      });

      if (authMode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          console.log('❌ Password mismatch detected');
          toast.error('Passwords do not match');
          return;
        }
        
        console.log('🚀 Calling signUpWithEmail...');
        const result = await signUpWithEmail(
          formData.email, 
          formData.password, 
          formData.displayName
        );
        
        console.log('📤 signUpWithEmail result:', result);
        
        if (result.success) {
          console.log('✅ Account creation successful, switching to signin mode');
          setAuthMode('signin');
          setFormData({ email: '', password: '', displayName: '', confirmPassword: '' });
        } else {
          console.log('❌ Account creation failed:', result.error);
        }
      } else {
        console.log('🚀 Calling signInWithEmail...');
        const result = await signInWithEmail(formData.email, formData.password);
        console.log('📤 signInWithEmail result:', result);
        
        if (result.success) {
          console.log('✅ Sign in successful, closing modal');
          onClose();
        } else {
          console.log('❌ Sign in failed:', result.error);
        }
      }
    } catch (error) {
      console.error('❌ Email auth error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      // Try simple password reset first (skips Firestore validation)
      const result = await sendUserPasswordResetEmailSimple(resetEmail);
      if (result.success) {
        setAuthMode('signin');
        setResetEmail('');
        setFormData({ email: resetEmail, password: '', displayName: '', confirmPassword: '' });
      }
    } catch (error) {
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', displayName: '', confirmPassword: '' });
    setResetEmail('');
    setShowPassword(false);
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`w-full max-w-md rounded-xl shadow-2xl p-6 ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {authMode === 'signin' && 'Sign In'}
              {authMode === 'signup' && 'Create Account'}
              {authMode === 'forgot-password' && 'Reset Password'}
            </h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <X size={20} />
            </button>
          </div>

          {/* Forgot Password Form */}
          {authMode === 'forgot-password' ? (
            <div>
              <div className="text-center mb-6">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </div>

              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Mail className="inline w-4 h-4 mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <Mail size={16} />
                  <span>{loading ? 'Sending...' : 'Send Reset Instructions'}</span>
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => switchMode('signin')}
                  className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors flex items-center justify-center space-x-2 mx-auto`}
                >
                  <ArrowLeft size={16} />
                  <span>Back to Sign In</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Google Sign In - Temporarily Disabled */}
              {false && (
                <>
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className={`w-full flex items-center justify-center space-x-3 py-3 px-4 rounded-lg border transition-colors mb-4 ${
                      darkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-white' 
                        : 'border-gray-300 hover:bg-gray-50 text-gray-900'
                    } disabled:opacity-50`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Continue with Google</span>
                  </button>

                  {/* Divider */}
                  <div className="relative mb-4">
                    <div className={`absolute inset-0 flex items-center ${
                      darkMode ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      <div className={`w-full border-t ${
                        darkMode ? 'border-gray-600' : 'border-gray-300'
                      }`}></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className={`px-2 ${
                        darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'
                      }`}>
                        Or continue with email
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Email Form */}
              <form onSubmit={handleEmailAuth} className="space-y-4">
                {authMode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <User className="inline w-4 h-4 mr-2" />
                      Display Name
                    </label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Mail className="inline w-4 h-4 mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Lock className="inline w-4 h-4 mr-2" />
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {authMode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Lock className="inline w-4 h-4 mr-2" />
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Please wait...' : (authMode === 'signin' ? 'Sign In' : 'Create Account')}
                </button>
              </form>

              {/* Forgot Password Link - Only show on sign in */}
              {authMode === 'signin' && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => switchMode('forgot-password')}
                    className="text-blue-600 hover:text-blue-700 text-sm transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {/* Switch Mode */}
              <div className="mt-6 text-center">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {authMode === 'signin' ? "Don't have an account?" : "Already have an account?"}
                  <button
                    onClick={() => switchMode(authMode === 'signin' ? 'signup' : 'signin')}
                    className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {authMode === 'signin' ? 'Sign up' : 'Sign in'}
                  </button>
                </p>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserAuth; 